import { EmblaOptionsType } from 'embla-carousel'
import useEmblaCarousel from 'embla-carousel-react'
import React, { useCallback, useEffect, useState } from 'react'

import { DotButton } from '@/app/components/common/DotButton'
import {
	NextButton,
	PrevButton,
} from '@/app/components/HotelCardCarousel/HotelCardCarouselArrows'
import HotelCardImage from '@/app/components/HotelCardCarousel/HotelCardImage'
import { FALLBACK_IMAGE } from '@/app/constants'

type PropType = {
	slides: string[]
}

const HotelCardCarousel: React.FC<PropType> = (props) => {
	const OPTIONS: EmblaOptionsType = { loop: true }
	const { slides } = props
	const [emblaRef, emblaApi] = useEmblaCarousel(OPTIONS)
	const [selectedIndex, setSelectedIndex] = useState(0)
	const [scrollSnaps, setScrollSnaps] = useState<number[]>([])

	const scrollTo = useCallback(
		(index: number) => emblaApi && emblaApi.scrollTo(index),
		[emblaApi]
	)

	const onSelect = useCallback(() => {
		if (!emblaApi) return
		setSelectedIndex(emblaApi.selectedScrollSnap())
	}, [emblaApi, setSelectedIndex])

	useEffect(() => {
		if (!emblaApi) return

		onSelect()
		setScrollSnaps(emblaApi.scrollSnapList())
		emblaApi.on('select', onSelect)
		emblaApi.on('reInit', onSelect)

		return () => {
			emblaApi.off('select', onSelect)
			emblaApi.off('reInit', onSelect)
		}
	}, [emblaApi, onSelect])

	// Don't render the carousel if there are no valid slides
	if (slides.length === 0) {
		return (
			<div className="hotel-card-carousel">
				<div className="hotel-card-carousel__viewport">
					<div className="hotel-card-carousel__container">
						<div className="hotel-card-carousel__slide">
							<div className="relative h-full w-full xlg:w-full">
								<HotelCardImage
									src={FALLBACK_IMAGE}
									fallbackSrc={FALLBACK_IMAGE}
									alt="Hotel fallback image"
									className="hotel-card-carousel__image object-cover w-full h-full"
									fill
									sizes="(max-width: 1023.70px) 100vw, (max-width: 1279.70px) 192px, 100vw"
									loading="lazy"
								/>
							</div>
						</div>
					</div>
				</div>
			</div>
		)
	}

	return (
		<div className="hotel-card-carousel">
			<div className="hotel-card-carousel__viewport" ref={emblaRef}>
				<div className="hotel-card-carousel__container">
					{slides.map((imageUrl, index) => (
						<div className="hotel-card-carousel__slide" key={index}>
							<div className="relative w-full xlg:w-full pb-[55%] lg:pb-[79.14%] max-sm:pb-0 max-sm:h-full">
								<HotelCardImage
									src={imageUrl}
									fallbackSrc={FALLBACK_IMAGE}
									alt={`Hotel view ${index + 1}`}
									className="hotel-card-carousel__image object-cover"
									fill
									sizes="(max-width: 1023.70px) 100vw, (max-width: 1279.70px) 100vw"
									loading="lazy"
								/>
							</div>
						</div>
					))}
				</div>
			</div>

			{slides.length > 1 && (
				<>
					<div className="hotel-card-carousel__controls">
						<PrevButton onClick={() => emblaApi?.scrollPrev()} />
						<NextButton onClick={() => emblaApi?.scrollNext()} />
					</div>

					<div className="hotel-card-carousel__dots">
						{scrollSnaps.map((_, index) => (
							<DotButton
								key={index}
								onClick={() => scrollTo(index)}
								className={`hotel-card-carousel__dot${index === selectedIndex
									? ' hotel-card-carousel__dot--selected'
									: ''
									}`}
							/>
						))}
					</div>
				</>
			)}
		</div>
	)
}

export default HotelCardCarousel
